import { Search } from "@tine/designsystem-ui-react";
import { FocusTrap } from "focus-trap-react";
import debounce from "lodash-es/debounce";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import { type ChangeEventHandler, lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "wouter";
import type { SupportedLocaleType } from "../../common/types/languageTypes";

import { SEARCH_TYPE } from "../../common/types/searchTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { mediaQueries } from "../../common/utils";
import useSupportedLocale from "../../common/utils/useSupportedLocale";
import featuresStore from "../../stores/features/featuresStore";

import searchStore from "../../stores/search/searchStore";
import theme from "../../themes/theme";

const hide = () => searchStore.clear(SEARCH_TYPE.QuickSearch, true);
const QuickSearchResultsDropdown = lazy(() => import("./QuickSearchResultsDropdown"));

const performQuickSearch = debounce((locale: SupportedLocaleType) => {
   searchStore.performSearches(SEARCH_TYPE.QuickSearch, locale);
}, 85);
const searchDomains = theme.searchConfig ? theme.searchConfig.searchDomains : [];

const SearchField = () => {
   const [location, redirectTo] = useLocation();
   //const quickSearchRef = useRef<HTMLDivElement>(null);
   const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
   const locale = useSupportedLocale();
   const isXl = useMediaQuery(mediaQueries.xl);
   const isLg = useMediaQuery(mediaQueries.lg);

   const results = searchStore[SEARCH_TYPE.QuickSearch].results;

   const totalHits: number = results.reduce((total, result) => total + result.hits.length, 0);

   const hasHits: boolean = !!totalHits;

   const isOpen = hasHits && !!searchStore.query;

   const resultsWithHits = results.filter(
      (result) => result.hits.length && (featuresStore.productNavigationAvailable || result.searchDomainType !== "product")
   );

   const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && searchStore.query !== "") {
         redirectTo(`/search?q=${searchStore.query}`);
         hide();
      }
      if (e.key === "Escape") {
         hide();
      }
   };

   const handleChange: ChangeEventHandler<HTMLInputElement> = action((e) => {
      searchStore.query = e.target.value;
      performQuickSearch(locale);
   });
   // Clear the search field if you navigate to a new page, except for the search result page
   useEffect(() => {
      if (location !== "/search") {
         hide();
      }
   }, [location]);

   if (!theme.searchConfig) {
      return null;
   }

   return (
      <FocusTrap active={isOpen}>
         <div>
            <div ref={setReferenceElement}>
               <Search
                  onChange={handleChange}
                  placeholder="Søk i hele butikken"
                  label=""
                  isLabelHidden={true}
                  value={searchStore.query}
                  autoComplete="off"
                  onKeyDown={handleKeyDown}
                  data-testid="search-field"
               />
            </div>

            {!!referenceElement && (
               <Suspense fallback={null}>
                  <QuickSearchResultsDropdown
                     isOpen={isOpen}
                     referenceElement={referenceElement}
                     resultsWithHits={resultsWithHits}
                     searchDomains={searchDomains}
                     hide={hide}
                     isLg={isLg}
                     isXl={isXl}
                  />
               </Suspense>
            )}
         </div>
      </FocusTrap>
   );
};

export default observer(SearchField);
